import React, { useState } from 'react';
import { useApp } from '../../context/AppContext';
import './CaseStatus.css';

const CaseStatusTracking = () => {
  const { cases, hearings, judgments } = useApp();
  const [searchType, setSearchType] = useState('caseNumber');
  const [searchValue, setSearchValue] = useState('');
  const [searchResults, setSearchResults] = useState([]);

  const handleSearch = (e) => {
    e.preventDefault();
    
    if (!searchValue.trim()) {
      alert('Please enter a search value');
      return;
    }

    let results = [];

    if (searchType === 'caseNumber') {
      results = cases.filter(c => 
        c.caseNumber.toLowerCase().includes(searchValue.toLowerCase())
      );
    } else if (searchType === 'partyName') {
      results = cases.filter(c => 
        (c.parties || []).some(p => 
          p.partyName.toLowerCase().includes(searchValue.toLowerCase())
        )
      );
    } else if (searchType === 'advocateName') {
      results = cases.filter(c => 
        (c.advocateMappings || []).some(m => 
          m.advocateName.toLowerCase().includes(searchValue.toLowerCase())
        )
      );
    }

    setSearchResults(results);
  };

  const getCaseDetails = (caseItem) => {
    const caseHearings = hearings.filter(h => h.caseNumber === caseItem.caseNumber);
    const nextHearing = caseHearings
      .filter(h => h.status === 'Scheduled' && new Date(h.hearingDate) >= new Date())
      .sort((a, b) => new Date(a.hearingDate) - new Date(b.hearingDate))[0];
    
    const caseJudgment = judgments.find(j => j.caseNumber === caseItem.caseNumber);

    return {
      hearings: caseHearings,
      nextHearing,
      judgment: caseJudgment,
    };
  };

  return (
    <div className="case-status-container">
      <div className="case-status-header">
        <h1>🔍 Case Status Tracking</h1>
        <p>Search for case information by Case Number, Party Name, or Advocate Name</p>
      </div>

      <div className="case-status-search">
        <form onSubmit={handleSearch}>
          <div className="search-filters">
            <div className="filter-group">
              <label>Search By</label>
              <select
                value={searchType}
                onChange={(e) => {
                  setSearchType(e.target.value);
                  setSearchValue('');
                  setSearchResults([]);
                }}
              >
                <option value="caseNumber">Case Number</option>
                <option value="partyName">Party Name</option>
                <option value="advocateName">Advocate Name</option>
              </select>
            </div>

            <div className="filter-group flex-1">
              <label>Search Value</label>
              <input
                type="text"
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
                placeholder={`Enter ${searchType === 'caseNumber' ? 'Case Number' : searchType === 'partyName' ? 'Party Name' : 'Advocate Name'}`}
              />
            </div>

            <div className="filter-group">
              <label>&nbsp;</label>
              <button type="submit" className="btn-primary">
                Search
              </button>
            </div>
          </div>
        </form>
      </div>

      {searchResults.length > 0 && (
        <div className="case-status-results">
          <h2>Search Results ({searchResults.length})</h2>
          {searchResults.map(caseItem => {
            const details = getCaseDetails(caseItem);
            return (
              <div key={caseItem.id} className="case-status-card">
                <div className="case-status-header-card">
                  <div>
                    <h3>{caseItem.caseNumber}</h3>
                    <p className="case-title">{caseItem.caseTitle}</p>
                  </div>
                  <span className={`status-badge status-${caseItem.status.toLowerCase().replace(' ', '-')}`}>
                    {caseItem.status}
                  </span>
                </div>

                <div className="case-status-details">
                  <div className="detail-section">
                    <h4>Case Information</h4>
                    <div className="detail-grid">
                      <div className="detail-item">
                        <strong>Type:</strong> {caseItem.caseType}
                      </div>
                      <div className="detail-item">
                        <strong>Court:</strong> {caseItem.courtName}
                      </div>
                      <div className="detail-item">
                        <strong>Judge:</strong> {caseItem.judgeName}
                      </div>
                      <div className="detail-item">
                        <strong>Filing Date:</strong> {new Date(caseItem.filingDate).toLocaleDateString()}
                      </div>
                    </div>
                  </div>

                  {caseItem.parties && caseItem.parties.length > 0 && (
                    <div className="detail-section">
                      <h4>Parties</h4>
                      <div className="parties-list">
                        {caseItem.parties.map(party => (
                          <div key={party.id} className="party-item">
                            <strong>{party.partyName}</strong> ({party.partyType})
                            {party.advocateName && <span> - Advocate: {party.advocateName}</span>}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {details.nextHearing && (
                    <div className="detail-section">
                      <h4>Next Hearing</h4>
                      <div className="hearing-info">
                        <strong>Date:</strong> {new Date(details.nextHearing.hearingDate).toLocaleDateString()}
                        <br />
                        <strong>Time:</strong> {details.nextHearing.hearingTime}
                        <br />
                        <strong>Court:</strong> {details.nextHearing.courtName}
                        <br />
                        <strong>Bench:</strong> {details.nextHearing.benchName || 'N/A'}
                      </div>
                    </div>
                  )}

                  {details.judgment && (
                    <div className="detail-section">
                      <h4>Judgment</h4>
                      <div className="judgment-info">
                        <strong>Date:</strong> {new Date(details.judgment.judgmentDate).toLocaleDateString()}
                        <br />
                        <strong>Type:</strong> {details.judgment.judgmentType}
                        <br />
                        <strong>Status:</strong> {details.judgment.status}
                      </div>
                    </div>
                  )}

                  {details.hearings.length > 0 && (
                    <div className="detail-section">
                      <h4>Hearing History</h4>
                      <div className="hearing-history">
                        {details.hearings.slice(0, 5).map(hearing => (
                          <div key={hearing.id} className="hearing-item">
                            {new Date(hearing.hearingDate).toLocaleDateString()} at {hearing.hearingTime} - 
                            <span className={`status-badge status-${hearing.status.toLowerCase()}`}>
                              {hearing.status}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {searchResults.length === 0 && searchValue && (
        <div className="case-status-no-results">
          <p>No cases found matching your search criteria.</p>
        </div>
      )}
    </div>
  );
};

export default CaseStatusTracking;

